/**
 * Tiny IndexedDB outbox for batches that could not be sent (offline PWA,
 * server down, page closing). Oldest first; capped so a long offline phase
 * can't fill the device. Falls back to memory when IndexedDB is missing.
 */
const DB_NAME = 'flm9-telemetry';
const STORE = 'outbox';
export const MAX_STORED_BATCHES = 200;

export interface StoredBatch {
  id?: number;
  body: string;
  storedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;
const memory: StoredBatch[] = [];

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const req = run(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function storeBatch(body: string): Promise<void> {
  const db = await openDb();
  const item: StoredBatch = { body, storedAt: Date.now() };
  if (!db) {
    memory.push(item);
    while (memory.length > MAX_STORED_BATCHES) memory.shift();
    return;
  }
  await tx(db, 'readwrite', (s) => s.add(item));
  const count = (await tx(db, 'readonly', (s) => s.count())) ?? 0;
  if (count > MAX_STORED_BATCHES) {
    const keys =
      (await tx(db, 'readonly', (s) => s.getAllKeys(null, count - MAX_STORED_BATCHES))) ?? [];
    for (const k of keys) await tx(db, 'readwrite', (s) => s.delete(k));
  }
}

/** The oldest stored batch, or null. */
export async function peekBatch(): Promise<StoredBatch | null> {
  const db = await openDb();
  if (!db) return memory[0] ?? null;
  const rows = (await tx(db, 'readonly', (s) => s.getAll(null, 1))) as StoredBatch[] | null;
  return rows?.[0] ?? null;
}

export async function dropBatch(batch: StoredBatch): Promise<void> {
  const db = await openDb();
  if (!db || batch.id === undefined) {
    const i = memory.indexOf(batch);
    if (i >= 0) memory.splice(i, 1);
    return;
  }
  await tx(db, 'readwrite', (s) => s.delete(batch.id!));
}

/** Test hook. */
export async function clearOutbox(): Promise<void> {
  memory.length = 0;
  const db = await openDb();
  if (db) await tx(db, 'readwrite', (s) => s.clear());
}
